import { useState, useEffect, useCallback } from "react";
import client, { apiErr } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Clock, Loader2, Sunrise, Sunset, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { confirm } from "@/components/ConfirmDialog";

function getGeo() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("no geo"));
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
      () => reject(new Error("denied")),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  });
}

const fmtT = (ts) => (ts ? new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—");

// Siteless "start my day / end my day" clock. Personal to the logged-in user.
// canDelete (admins/owners) reveals a control to remove an erroneous entry.
export function WorkdayCard({ canDelete = false }) {
  const { t } = useI18n();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadStatus = useCallback(() => client.get("/attendance/workday/status").then((r) => setStatus(r.data)).catch(() => {}), []);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  const act = async (path) => {
    setBusy(true);
    try {
      let geo = null;
      try { geo = await getGeo(); } catch { /* denied/unavailable */ }
      if (path === "start" && !geo) {
        toast.error(t("wd.locNeeded", "A valid location is required to start your day — please allow location access."));
        return;
      }
      await client.post(`/attendance/workday/${path}`, geo ? { geo } : {});
      toast.success(path === "start" ? t("wd.started", "Your workday has started") : t("wd.ended", "Your workday has ended"));
      loadStatus();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const deleteWorkday = async () => {
    if (!status?.workdayId) return;
    if (!(await confirm({ title: t("wd.delTitle", "Delete workday entry"), description: t("wd.delConfirm", "Remove today's workday entry? This cannot be undone."), confirmText: t("common.delete", "Delete"), destructive: true }))) return;
    try { await client.delete(`/attendance/workday/${status.workdayId}`); toast.success(t("wd.deleted", "Workday entry deleted")); loadStatus(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const onWorkday = status?.onWorkday;
  const label = onWorkday
    ? `${t("wd.workingSince", "Working since")} ${fmtT(status.startedAt)}`
    : status?.endedAt
      ? `${t("wd.endedAt", "Day ended at")} ${fmtT(status.endedAt)}`
      : t("wd.notStarted", "Your day hasn't started yet");

  return (
    <Card className="p-4 border-slate-200" data-testid="workday-card">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${onWorkday ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
            {onWorkday ? <Sunrise className="h-5 w-5" /> : <Clock className="h-5 w-5" />}
          </div>
          <div>
            <p className="font-semibold text-slate-900" data-testid="workday-status">{label}</p>
            <p className="text-xs text-slate-400">{t("wd.subtitle", "Track your office working day")}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canDelete && status?.workdayId && (
            <Button variant="ghost" size="icon" data-testid="workday-delete-btn" onClick={deleteWorkday} className="text-slate-400 hover:text-red-600 hover:bg-red-50" title={t("wd.delTitle", "Delete workday entry")}><Trash2 className="h-4 w-4" /></Button>
          )}
          {onWorkday ? (
            <Button data-testid="workday-end-btn" onClick={() => act("end")} disabled={busy} className="bg-brand hover:bg-brand-dark">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sunset className="h-4 w-4 mr-2" />{t("wd.endDay", "End my day")}</>}
            </Button>
          ) : (
            <Button data-testid="workday-start-btn" onClick={() => act("start")} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sunrise className="h-4 w-4 mr-2" />{t("wd.startDay", "Start my day")}</>}
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}
